import CloseIcon from "@mui/icons-material/Close";
import Logger from "../../services/Logger";
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import React, { useEffect, useState } from "react";
import { BootstrapDialog } from "../components/BootstrapDialogTitle";
import ReferralService from "./ReferralService";

export default function CreateReferralEventDialog({ open, setOpen, callback }) {
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const calculateEndDate = (startDate) => {
        if (!startDate) return "";
        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(end.getDate() + 14); // Add 2 weeks
        const year = end.getFullYear();
        const month = String(end.getMonth() + 1).padStart(2, "0");
        const day = String(end.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const isTwoWeeksDuration = (startDate, endDate) => {
        if (!startDate || !endDate) return false;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = end - start;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays === 14;
    };

    const getDurationDays = (startDate, endDate) => {
        if (!startDate || !endDate) return null;
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = end - start;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const [formData, setFormData] = useState(() => {
        const today = getTodayDate();
        return {
            name: "",
            startTs: today,
            endTs: calculateEndDate(today),
            requiredCount: 5,
            rewardMonths: 1,
        };
    });

    const [twoWeeksChecked, setTwoWeeksChecked] = useState(true);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            const today = getTodayDate();
            const endDate = calculateEndDate(today);
            setFormData({
                name: "",
                startTs: today,
                endTs: endDate,
                requiredCount: 5,
                rewardMonths: 1,
            });
            setTwoWeeksChecked(true);
            setErrors({});
        }
    }, [open]);

    const handleClose = () => {
        setOpen(false);
        const today = getTodayDate();
        const endDate = calculateEndDate(today);
        setFormData({
            name: "",
            startTs: today,
            endTs: endDate,
            requiredCount: 5,
            rewardMonths: 1,
        });
        setTwoWeeksChecked(true);
        setErrors({});
    };

    const handleFormChange = (event) => {
        const { name, value, type, checked } = event.target;
        
        if (name === "twoWeeks") {
            setTwoWeeksChecked(checked);
            if (checked && formData.startTs) {
                const endDate = calculateEndDate(formData.startTs);
                setFormData((prevData) => ({
                    ...prevData,
                    endTs: endDate,
                }));
            }
            return;
        }

        setFormData((prevData) => {
            const newData = {
                ...prevData,
                [name]: type === "checkbox" ? checked : value,
            };
            
            // If start date changes and 2 weeks checkbox is checked, update end date
            if (name === "startTs" && twoWeeksChecked && value) {
                newData.endTs = calculateEndDate(value);
            }
            
            // If end date changes, check if it's exactly 2 weeks and update checkbox
            if (name === "endTs" && value && prevData.startTs) {
                const isTwoWeeks = isTwoWeeksDuration(prevData.startTs, value);
                if (!isTwoWeeks && twoWeeksChecked) {
                    setTwoWeeksChecked(false);
                } else if (isTwoWeeks && !twoWeeksChecked) {
                    setTwoWeeksChecked(true);
                }
            }
            
            // If start date changes and we have an end date, check if it's still 2 weeks
            if (name === "startTs" && value && prevData.endTs) {
                const isTwoWeeks = isTwoWeeksDuration(value, prevData.endTs);
                if (!isTwoWeeks && twoWeeksChecked) {
                    setTwoWeeksChecked(false);
                } else if (isTwoWeeks && !twoWeeksChecked) {
                    setTwoWeeksChecked(true);
                }
            }
            
            return newData;
        });
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [name]: "",
            }));
        }
    };

    const validate = () => {
        const { name, startTs, endTs, requiredCount, rewardMonths } = formData;
        const newErrors = {};

        if (!name || !name.trim()) {
            newErrors.name = "Name is required";
        }

        if (!startTs) {
            newErrors.startTs = "Start date is required";
        }

        if (!endTs) {
            newErrors.endTs = "End date is required";
        }

        if (startTs && endTs) {
            const startDate = new Date(startTs);
            const endDate = new Date(endTs);
            if (endDate <= startDate) {
                newErrors.endTs = "End date must be after start date";
            }
        }

        if (!requiredCount || requiredCount <= 0) {
            newErrors.requiredCount = "Required count must be greater than 0";
        }

        if (!rewardMonths || rewardMonths <= 0) {
            newErrors.rewardMonths = "Reward months must be greater than 0";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreate = async () => {
        if (!validate()) {
            return;
        }

        setIsSubmitting(true);
        try {
            // Convert date to timestamp (start of day for start, end of day for end)
            const startDate = new Date(formData.startTs);
            startDate.setHours(0, 0, 0, 0);
            const startTs = startDate.getTime();
            
            const endDate = new Date(formData.endTs);
            endDate.setHours(23, 59, 59, 999);
            const endTs = endDate.getTime();
            
            const requiredCount = parseInt(formData.requiredCount);
            const rewardMonths = parseInt(formData.rewardMonths);

            await ReferralService.createEvent(
                formData.name.trim(),
                startTs,
                endTs,
                requiredCount,
                rewardMonths
            );

            handleClose();
            callback();
        } catch (error) {
            Logger.error("Error creating referral event:", error);
            setErrors({
                submit: error.message || "Failed to create referral event",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <React.Fragment>
            <BootstrapDialog
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
                sx={{
                    "& .MuiDialog-paper": {
                        width: 500,
                        margin: 0,
                    },
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                    Create Referral Event
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Event Name"
                                fullWidth
                                variant="outlined"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                error={Boolean(errors.name)}
                                helperText={errors.name || "Examples: Boost-5 Event, Circle-5 Reward, Referral Sprint"}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                margin="dense"
                                label="Start Date"
                                fullWidth
                                type="date"
                                variant="outlined"
                                name="startTs"
                                value={formData.startTs}
                                onChange={handleFormChange}
                                error={Boolean(errors.startTs)}
                                helperText={errors.startTs}
                                slotProps={{
                                    input: {
                                        shrink: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                margin="dense"
                                label="End Date"
                                fullWidth
                                type="date"
                                variant="outlined"
                                name="endTs"
                                value={formData.endTs}
                                onChange={handleFormChange}
                                error={Boolean(errors.endTs)}
                                helperText={errors.endTs}
                                slotProps={{
                                    input: {
                                        shrink: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={twoWeeksChecked}
                                        onChange={handleFormChange}
                                        name="twoWeeks"
                                    />
                                }
                                label="2 weeks"
                            />
                            {formData.startTs && formData.endTs && !isTwoWeeksDuration(formData.startTs, formData.endTs) && (
                                <span style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "0.875rem" }}>
                                    {getDurationDays(formData.startTs, formData.endTs)} days
                                </span>
                            )}
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                margin="dense"
                                label="Required Count"
                                fullWidth
                                type="number"
                                variant="outlined"
                                name="requiredCount"
                                value={formData.requiredCount}
                                onChange={handleFormChange}
                                error={Boolean(errors.requiredCount)}
                                helperText={errors.requiredCount}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                margin="dense"
                                label="Reward Months"
                                fullWidth
                                type="number"
                                variant="outlined"
                                name="rewardMonths"
                                value={formData.rewardMonths}
                                onChange={handleFormChange}
                                error={Boolean(errors.rewardMonths)}
                                helperText={errors.rewardMonths}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        {errors.submit && (
                            <Grid size={{ xs: 12 }}>
                                <div style={{ color: "red", fontSize: "0.875rem", marginTop: "8px" }}>
                                    {errors.submit}
                                </div>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleCreate} disabled={isSubmitting}>
                        {isSubmitting ? "Creating..." : "Create"}
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </React.Fragment>
    );
}

